// ── UPI deep links ──────────────────────────────────────────────────────────────
// Mirror of buildUpiUri in server/party-bill.js. Keep the two in step — the server builds the link for
// the group join fee and the party bill, the client builds it for a shop listing.
//
// Do NOT reach for URLSearchParams here. That is form-encoding: it spells a space as "+" (a literal
// plus in a URI query, so a payee name arrives as "Shweta+Garg") and escapes the VPA's "@" as "%40",
// which is what makes UPI apps refuse the intent with a generic "Something went wrong."

// Parse a UPI payment string — the mirror of parseUpiString in server/party-bill.js (canonical, and
// unit-tested there; keep the two in step). Used here on whatever a scanned/uploaded QR decodes to,
// so that a payee who uploaded only a picture still yields a VPA we can build a deep link from.
// Accepts:
//   upi://pay?pa=merchant@upi&pn=Shop&am=350.00&cu=INR
//   upi:pay?pa=...  (some QR generators omit the //)
//   raw VPA like merchant@okaxis
// Returns { vpa, name, amountPaise, currency } or null if unparseable.
export function parseUpiString(input) {
  if (!input || typeof input !== 'string') return null;
  const s = input.trim();

  try {
    const url = new URL(s.startsWith('upi:') ? s : `upi://${s}`);
    // The `pa` guard is what stops a raw VPA ("vendor@ybl" → upi://vendor@ybl, host ybl, user
    // vendor) matching as a URL, finding no pa, and returning null instead of falling through.
    if (url.protocol === 'upi:' && url.searchParams.has('pa')) {
      const p = url.searchParams;
      const vpa = p.get('pa') || '';
      if (!vpa) return null;
      const amStr = p.get('am');
      return {
        vpa,
        name: p.get('pn') || '',
        amountPaise: amStr != null && amStr !== '' ? Math.round(parseFloat(amStr) * 100) : null,
        currency: (p.get('cu') || 'INR').toUpperCase(),
      };
    }
  } catch { /* not a URL — fall through to the raw-VPA check */ }

  if (/^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z][a-zA-Z0-9.\-_]{1,64}$/.test(s)) {
    return { vpa: s, name: '', amountPaise: null, currency: 'INR' };
  }
  return null;
}

// Does this look like a payee address we can actually send money to? A decoded QR can contain
// anything — a website, a wifi config, a UPI *mandate* — and none of that belongs in a payment
// field, so a decode is only trusted when the VPA passes this.
export function isLikelyVpa(value) {
  return /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z][a-zA-Z0-9.\-_]{1,64}$/.test(String(value || '').trim());
}

// UPI apps accept a narrow, plain-ASCII payee name and note. Group and product names routinely carry
// emoji and punctuation, which a strict PSP app rejects. 50 chars is the UPI limit for `tn`.
export function upiSafeText(value, maxLen = 50) {
  return String(value ?? '')
    .replace(/[^A-Za-z0-9 .\-_,()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLen)
    .trim();
}

// ── Which app opens a upi:// link ───────────────────────────────────────────────
// Nothing here is a spec question, it's an OS one. iOS has no app chooser: the first installed app
// that registered the `upi://` scheme wins, and on many phones that is WhatsApp. WhatsApp Pay then
// shows "Something went wrong" because it can't pay an arbitrary VPA by intent (or isn't onboarded),
// which looks exactly like a broken payment link even when the URI is perfect.
//
// So don't hand the user one generic link and hope. Offer their actual app by its own scheme, and
// treat the QR — scanned from *inside* a UPI app — as the path that always works.
// The scheme an app answers to is not always the same on both platforms, and a custom-scheme link
// with no installed handler does *nothing at all* on iOS — no error, no prompt. So a wrong scheme
// here doesn't look like a wrong scheme, it looks like a dead button.
const UPI_APPS = [
  { id: 'gpay',    label: 'Google Pay',
    // Google Pay India still registers `tez://` on iOS, from its original Tez identity; `gpay://` is
    // the Android one. Handing an iPhone gpay:// is a link to nowhere.
    scheme: (q, ios) => (ios ? `tez://upi/pay?${q}` : `gpay://upi/pay?${q}`) },
  { id: 'phonepe', label: 'PhonePe',    scheme: (q) => `phonepe://pay?${q}` },
  { id: 'paytm',   label: 'Paytm',      scheme: (q) => `paytmmp://pay?${q}` },
  { id: 'bhim',    label: 'BHIM',       scheme: (q) => `bhim://pay?${q}` },
];

// Per-app deep links for the same payment, plus the generic one last.
// Best-effort: a scheme only opens if that app is installed, and there is no reliable way to detect
// installation from a browser — which is exactly why the QR stays the primary route.
export function buildUpiAppLinks(payment, { ios = false } = {}) {
  const generic = buildUpiUri(payment);
  if (!generic) return [];
  const query = generic.slice('upi://pay?'.length);
  return [
    ...UPI_APPS.map(a => ({ id: a.id, label: a.label, href: a.scheme(query, ios) })),
    { id: 'any', label: 'Any UPI app', href: generic },
  ];
}

export function buildUpiUri({ vpa, name, amountPaise, note, currency = 'INR' }) {
  const payee = String(vpa ?? '').trim();
  if (!payee) return null;

  // "@" and "." are legal, expected characters in a query value — leave them literal.
  const encodedVpa = encodeURIComponent(payee).replace(/%40/g, '@').replace(/%2E/gi, '.');
  const parts = [`pa=${encodedVpa}`];

  const payeeName = upiSafeText(name);
  if (payeeName) parts.push(`pn=${encodeURIComponent(payeeName)}`);
  if (amountPaise != null) parts.push(`am=${(amountPaise / 100).toFixed(2)}`);
  parts.push(`cu=${encodeURIComponent(currency || 'INR')}`);
  const safeNote = upiSafeText(note);
  if (safeNote) parts.push(`tn=${encodeURIComponent(safeNote)}`);

  return `upi://pay?${parts.join('&')}`;
}
